'use client';

import { ArrowRight, ArrowRightLeft, Sparkles } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { type ReactNode, useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  checkCreateUserOfflineAvatar,
  deleteUserOfflineAvatar,
  listPublicOfflineAvatars,
  listUserOfflineAvatars,
} from '@/app/apis';
import { AgentLineHistory } from '@/app/components/icons/AgentLineHistory';
import { InfoIcon } from '@/app/components/icons/InfoIcon';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { Button } from '@/app/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/app/components/ui/dialog';
import { toast } from '@/app/components/ui/use-toast';
import { useFetch, useTaskSubmitError, useTrials } from '@/app/hooks';
import { useStore, useStoreApi } from '@/app/providers/store';
import type { OfflineAvatarInput } from '@/app/types';
import { cn, countLipSyncTextWeightedLength, getDefaultReportParams } from '@/app/utils';
import { retryPendingTempUploads } from '@/app/utils/client';
import { Link } from '@/i18n/routing';
import {
  AvatarGallery,
  CreateAvatarDialog,
  InlineActionTimeline,
  OfflineDigitalHumanSettings,
  SpeechComposer,
  type StudioAvatarItem,
  StudioHero,
  type StudioOutputSettings,
  StudioSteps,
  toStudioAvatarItem,
} from './_components';
import {
  appendStudioAction,
  getStudioReadiness,
  getStudioSpeech,
  type StudioAction,
  type StudioActionIssue,
  type StudioSpeechMode,
  type StudioStep,
  useOfflineAvatarTasks,
  useStudioMedia,
  validateStudioActionIssues,
  validateStudioActions,
} from './_hooks';
import {
  buildOfflineAvatarTaskParams,
  buildTtsTaskParams,
  OfflineAvatarTaskError,
  TtsGenerationError,
  type TtsResult,
} from './apis';
import { OFFLINE_AVATAR_MIN_TEXT_CHARACTERS } from './constants';

const OFFLINE_AVATAR_PAGE_SIZE = 50;

const DEFAULT_OUTPUT: StudioOutputSettings = {
  resolution: '720p',
  watermark: false,
  watermarkUrl: '',
  watermarkPosition: 'bottom_right',
};

export default function OfflineDigitalHumanForm() {
  const t = useTranslations('tools.offlineDigitalHuman.studio');
  const tOfflineAvatar = useTranslations('tools.offlineAvatar');
  const tAvatar = useTranslations('tools.offlineAvatar.avatar');
  const tCommon = useTranslations('common.dialog');
  const { userId, isFree, isUserInfoInitialized, trialsOfflineAvatar } = useStore((state) => ({
    userId: state.userInfo?.id,
    isFree: state.userInfo?.subs_plan === 'free',
    isUserInfoInitialized: state.isUserInfoInitialized,
    trialsOfflineAvatar: state.trialsOfflineAvatar,
  }));
  const { updateTrialsAsync } = useTrials();
  const { handleError: handleTtsSubmitError } = useTaskSubmitError({
    isFree,
    templateScene: '',
    taskType: 'tts_free',
  });
  const { handleError: handleOfflineAvatarSubmitError } = useTaskSubmitError({
    isFree,
    templateScene: '',
    taskType: 'offline_avatar',
  });
  const storeApi = useStoreApi();
  const openLoginDialog = () => storeApi.getState().openModal('streamLoginDialog');
  const [step, setStep] = useState<StudioStep>(0);
  const [selectedAvatar, setSelectedAvatar] = useState<string | null>(null);
  const [selectedAvatarAssetId, setSelectedAvatarAssetId] = useState<string | null>(null);
  const [selectedAvatarImage, setSelectedAvatarImage] = useState<string | null>(null);
  const [speechMode, setSpeechMode] = useState<StudioSpeechMode>('text');
  const [script, setScript] = useState('');
  const [selectedVoice, setSelectedVoice] = useState('');
  const [ttsResult, setTtsResult] = useState<(TtsResult & { fingerprint: string }) | null>(null);
  const [actionMode, setActionMode] = useState<'auto' | 'custom'>('auto');
  const [actionTimelineOpen, setActionTimelineOpen] = useState(false);
  const [actions, setActions] = useState<StudioAction[]>([]);
  const [draftActions, setDraftActions] = useState<StudioAction[]>([]);
  const [draftActionIssues, setDraftActionIssues] = useState<StudioActionIssue[]>([]);
  const [draftValidationActive, setDraftValidationActive] = useState(false);
  const [draftValidationTooltipOpen, setDraftValidationTooltipOpen] = useState(false);
  const [output, setOutput] = useState<StudioOutputSettings>(DEFAULT_OUTPUT);
  const [captureBusy, setCaptureBusy] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [submitSucceeded, setSubmitSucceeded] = useState(false);
  const [hasSubmittedTask, setHasSubmittedTask] = useState(false);
  const [createAvatarOpen, setCreateAvatarOpen] = useState(false);
  const [editingAvatar, setEditingAvatar] = useState<StudioAvatarItem | null>(null);
  const [actionRequiredTooltipOpen, setActionRequiredTooltipOpen] = useState(false);
  const submissionLock = useRef(false);
  const taskService = useOfflineAvatarTasks();
  const submitting = preparing || taskService.submitting;
  const scrollId = useId();
  const scrollRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const actionTimelineDialogRef = useRef<HTMLDivElement>(null);
  const [stageHeight, setStageHeight] = useState<number>();
  // 第 2 步提示词框的高度：左侧角色照片跟它等高，第 3 步沿用同一高度
  const [promptBoxHeight, setPromptBoxHeight] = useState<number>();
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => setStageHeight(Math.round(entry.contentRect.height)));
    observer.observe(stage);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    if (!submitSucceeded) return;
    const timeout = window.setTimeout(() => setSubmitSucceeded(false), 2000);
    return () => window.clearTimeout(timeout);
  }, [submitSucceeded]);
  useEffect(() => {
    if (userId) void updateTrialsAsync();
  }, [updateTrialsAsync, userId]);
  const media = useStudioMedia();
  const {
    data: officialAvatarsData,
    loading: officialAvatarsLoading,
    refresh: refreshOfficialAvatars,
  } = useFetch(() =>
    listPublicOfflineAvatars({ query: { 'pager.page': 0, 'pager.pagesz': OFFLINE_AVATAR_PAGE_SIZE } }),
  );
  const {
    data: userAvatarsData,
    loading: userAvatarsLoading,
    refresh: refreshUserAvatars,
  } = useFetch(() => listUserOfflineAvatars({ query: { 'pager.page': 0, 'pager.pagesz': OFFLINE_AVATAR_PAGE_SIZE } }), {
    suppressUnauthorizedLoginDialog: true,
  });
  const previousUserIdRef = useRef(userId);
  useEffect(() => {
    const previousUserId = previousUserIdRef.current;
    previousUserIdRef.current = userId;
    if (!previousUserId && userId) {
      refreshOfficialAvatars();
      refreshUserAvatars();
    }
  }, [refreshOfficialAvatars, refreshUserAvatars, userId]);
  const officialAvatars = useMemo(
    () => (officialAvatarsData?.data?.items ?? []).filter((avatar) => avatar.live_image).map(toStudioAvatarItem),
    [officialAvatarsData?.data?.items],
  );
  const userAvatars = useMemo(
    () => (userAvatarsData?.data?.items ?? []).filter((avatar) => avatar.live_image).map(toStudioAvatarItem),
    [userAvatarsData?.data?.items],
  );

  const selectedImage = selectedAvatar === media.myImage?.id ? media.myImage : null;
  const imageSource =
    selectedAvatarImage ||
    selectedImage?.prompt?.selectedImage ||
    selectedImage?.prompt?.sourceImage ||
    selectedImage?.prompt?.selectedImagePreview ||
    selectedImage?.prompt?.sourceImagePreview;
  const imagePreview =
    selectedAvatarImage || selectedImage?.prompt?.selectedImagePreview || selectedImage?.prompt?.sourceImagePreview;
  const activeAudio = speechMode === 'record' ? media.recordedPrompt : media.audioPrompt;
  const speechFingerprint = `${script.trim()}\u0000${selectedVoice}`;
  const reusableTts = ttsResult?.fingerprint === speechFingerprint ? ttsResult : null;
  const duration = speechMode === 'text' ? reusableTts?.duration : activeAudio?.prompt?.duration;
  const timelineDuration = duration === undefined ? undefined : Math.ceil(duration);
  const ready = getStudioReadiness({
    hasAvatar: Boolean(selectedAvatarImage || (selectedImage?.status === 'done' && selectedImage.prompt)),
    mode: speechMode,
    script,
    hasAudio: media.audioPrompt?.status === 'done' && Boolean(media.audioPrompt.prompt),
    hasRecording: media.recordedPrompt?.status === 'done' && Boolean(media.recordedPrompt.prompt),
  });
  const speechReady = ready[1] && (speechMode !== 'text' || Boolean(selectedVoice));
  const textWeightedLength = countLipSyncTextWeightedLength(script.trim());
  const textValidationIssues =
    speechMode === 'text'
      ? [
          ...(textWeightedLength === 0 ? [t('speech.textRequired')] : []),
          ...(textWeightedLength > 0 && textWeightedLength < OFFLINE_AVATAR_MIN_TEXT_CHARACTERS
            ? [t('speech.textTooShort', { count: OFFLINE_AVATAR_MIN_TEXT_CHARACTERS })]
            : []),
          ...(!selectedVoice ? [t('speech.voiceRequired')] : []),
        ]
      : [];
  const textValidationMessage =
    textValidationIssues.length > 1 ? (
      <ol className="list-decimal space-y-1 pl-5">
        {textValidationIssues.map((issue) => (
          <li key={issue}>{issue}</li>
        ))}
      </ol>
    ) : (
      (textValidationIssues[0] ?? null)
    );
  const preparedSpeechReady = speechMode !== 'text' || Boolean(reusableTts);
  const actionIssue = actionMode === 'custom' ? validateStudioActions(actions, timelineDuration) : null;
  const hasMissingActionDescription = actionMode === 'custom' && actions.some((action) => !action.prompt.trim());
  const mediaBusy =
    selectedImage?.status === 'uploading' || (speechMode !== 'text' && activeAudio?.status === 'uploading');
  const canContinueWithoutActionValidation =
    !captureBusy &&
    !mediaBusy &&
    (step === 0 ? ready[0] : step === 1 ? ready[0] && speechReady : ready[0] && speechReady && preparedSpeechReady);
  const canContinue = canContinueWithoutActionValidation && !actionIssue;
  const canClickGenerate = canContinueWithoutActionValidation && (actionIssue === null || hasMissingActionDescription);
  const remainingOfflineAvatarTrials = trialsOfflineAvatar?.remaining ?? 0;
  const hasOfflineAvatarTrialQuota = (trialsOfflineAvatar?.total ?? 0) > 0;
  const hasExhaustedTrials = hasOfflineAvatarTrialQuota && remainingOfflineAvatarTrials <= 0;
  const generateClickValidationMessage = hasExhaustedTrials
    ? tOfflineAvatar('trials.exhaustedDescription')
    : hasMissingActionDescription
      ? t('actions.required')
      : null;
  const generateDisabledMessage = !canClickGenerate
    ? captureBusy
      ? t(speechMode === 'record' ? 'speech.recording' : 'speech.audioUploading')
      : selectedImage?.status === 'uploading'
        ? t('avatar.uploading')
        : speechMode !== 'text' && activeAudio?.status === 'uploading'
          ? t('speech.audioUploading')
          : !ready[0]
            ? t('error.image')
            : speechMode === 'text' && textWeightedLength === 0
              ? t('speech.textRequired')
              : speechMode === 'text' && !ready[1]
                ? t('error.scriptTooLong')
                : speechMode === 'text' && !selectedVoice
                  ? t('speech.voiceRequired')
                  : !preparedSpeechReady
                    ? t('error.speech')
                    : actionIssue === 'range'
                      ? t('actions.range')
                      : actionIssue === 'overlap'
                        ? t('actions.overlap')
                        : actionIssue === 'exceeds'
                          ? t('actions.exceeds')
                          : t('error.speech')
    : null;
  const generateTooltipMessage = generateClickValidationMessage ?? generateDisabledMessage;

  useEffect(() => {
    if (!hasMissingActionDescription) setActionRequiredTooltipOpen(false);
  }, [hasMissingActionDescription]);

  const navigate = (next: StudioStep) => {
    setStep(next);
    requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = 0;
      composerRef.current?.focus({ preventScroll: true });
    });
  };
  const notifyTtsGeneratingLock = () => {
    toast({ type: 'warning', message: t('speech.generatingLocked') });
  };
  const selectAvatar = (avatar: StudioAvatarItem) => {
    if (taskService.generatingSpeech) {
      notifyTtsGeneratingLock();
      return;
    }
    const nextVoice = avatar.voiceId ?? '';
    setSelectedAvatar(avatar.id);
    setSelectedAvatarAssetId(avatar.avatarId ?? null);
    setSelectedAvatarImage(avatar.id === media.myImage?.id ? null : avatar.src);
    if (nextVoice !== selectedVoice) setTtsResult(null);
    setSelectedVoice(nextVoice);
  };
  const openCreateAvatar = async () => {
    if (!userId) {
      openLoginDialog();
      return;
    }
    try {
      const { data, error } = await checkCreateUserOfflineAvatar();
      if (error?.code === 429 || error?.reason === 'OfflineAvatarQuotaExceeded' || data?.ok === false) {
        toast({ type: 'error', message: tAvatar('createQuotaExceeded') });
        return;
      }
      if (error || !data) {
        toast({ type: 'error', message: tAvatar('createFailed') });
        return;
      }
      setEditingAvatar(null);
      setCreateAvatarOpen(true);
    } catch {
      toast({ type: 'error', message: tAvatar('createFailed') });
    }
  };
  const editAvatar = (avatar: StudioAvatarItem) => {
    setEditingAvatar(avatar);
    setCreateAvatarOpen(true);
  };
  const deleteAvatar = async (avatar: StudioAvatarItem) => {
    if (!window.confirm(tAvatar('deleteConfirmMessage'))) return;
    const { error } = await deleteUserOfflineAvatar({ path: { id: avatar.id } });
    if (error) {
      toast({ type: 'error', message: tAvatar('deleteFailed') });
      return;
    }
    if (selectedAvatar === avatar.id) {
      setSelectedAvatar(null);
      setSelectedAvatarAssetId(null);
      setSelectedAvatarImage(null);
    }
    void refreshUserAvatars();
    toast({ type: 'success', message: tAvatar('deleteSuccess') });
  };
  const changeActionMode = (value: 'auto' | 'custom') => {
    if (value === 'auto') {
      setActionMode('auto');
      setActionTimelineOpen(false);
      return;
    }
    setDraftActions(
      actions.length === 0
        ? appendStudioAction([], crypto.randomUUID(), timelineDuration)
        : actions.map((action) => ({ ...action })),
    );
    setDraftActionIssues([]);
    setDraftValidationActive(false);
    setDraftValidationTooltipOpen(false);
    setActionTimelineOpen(true);
  };
  const closeActionTimeline = () => {
    setActionTimelineOpen(false);
    setDraftActions([]);
    setDraftActionIssues([]);
    setDraftValidationActive(false);
    setDraftValidationTooltipOpen(false);
  };
  const confirmActionTimeline = () => {
    const issues = validateStudioActionIssues(draftActions, timelineDuration);
    if (issues.length > 0) {
      setDraftActionIssues(issues);
      setDraftValidationActive(true);
      setDraftValidationTooltipOpen(true);
      return;
    }
    setActions(draftActions.map((action) => ({ ...action })));
    setActionMode('custom');
    setActionTimelineOpen(false);
    setDraftActions([]);
    setDraftActionIssues([]);
    setDraftValidationActive(false);
    setDraftValidationTooltipOpen(false);
  };

  const continueFromSpeech = async () => {
    if (!ready[0] || !speechReady || textValidationIssues.length > 0 || taskService.generatingSpeech) return;
    if (speechMode !== 'text' || reusableTts) {
      navigate(2);
      return;
    }
    if (!userId) {
      openLoginDialog();
      return;
    }
    try {
      const result = await taskService.generateTts(script.trim(), selectedVoice);
      if (!result) return;
      setTtsResult({ ...result, fingerprint: speechFingerprint });
      navigate(2);
    } catch (error) {
      if (error instanceof TtsGenerationError && error.reason) {
        handleTtsSubmitError(
          { code: error.code ?? 0, reason: error.reason, message: error.message, metadata: error.metadata },
          getDefaultReportParams({ value: buildTtsTaskParams(script.trim(), selectedVoice), isInspireMe: false }),
        );
        return;
      }
      toast({ type: 'error', message: t('error.tts') });
    }
  };

  const changeStep = (next: StudioStep) => {
    if (taskService.generatingSpeech && next !== step) {
      notifyTtsGeneratingLock();
      return;
    }
    if (step === 1 && next === 2) {
      void continueFromSpeech();
      return;
    }
    navigate(next);
  };

  const submit = async () => {
    if (hasMissingActionDescription) {
      setActionRequiredTooltipOpen(true);
      return;
    }
    if (!canContinue || submissionLock.current || !imageSource) return;
    if (!userId) {
      openLoginDialog();
      return;
    }
    const taskInput: OfflineAvatarInput = {
      image: imageSource,
      speech:
        speechMode === 'text' && reusableTts
          ? { mode: 'audio', audio: reusableTts.uri, duration: reusableTts.duration }
          : getStudioSpeech({
              mode: speechMode,
              script,
              voice: selectedVoice,
              audio: media.audioPrompt?.prompt?.src || media.audioPrompt?.prompt?.preview || '',
              recording: media.recordedPrompt?.prompt?.src || media.recordedPrompt?.prompt?.preview || '',
              audioDuration: media.audioPrompt?.prompt?.duration,
              recordingDuration: media.recordedPrompt?.prompt?.duration,
            }),
      actions: actionMode === 'custom' ? actions.map(({ start, end, prompt }) => ({ start, end, prompt })) : undefined,
      resolution: output.resolution,
      watermark: output.watermark,
      watermarkUrl: output.watermarkUrl,
      watermarkPosition: output.watermarkPosition,
    };
    submissionLock.current = true;
    setSubmitSucceeded(false);
    setPreparing(true);
    try {
      await updateTrialsAsync();
      const latestTrials = storeApi.getState().trialsOfflineAvatar;
      const latestRemainingTrials = latestTrials?.remaining ?? 0;
      const latestHasTrialQuota = (latestTrials?.total ?? 0) > 0;
      if (latestHasTrialQuota && latestRemainingTrials <= 0) {
        setActionRequiredTooltipOpen(true);
        return;
      }
      const shouldUseTrial = latestRemainingTrials > 0;
      const retryResult = await retryPendingTempUploads();
      if (!retryResult.success) {
        toast({ type: 'error', message: t('error.submit') });
        return;
      }
      if (retryResult.retried) return;
      const task = await taskService.submit(taskInput, {
        avatarId: selectedAvatarAssetId ?? undefined,
        useTrial: shouldUseTrial,
      });
      if (task) {
        setSubmitSucceeded(true);
        setHasSubmittedTask(true);
        if (shouldUseTrial) void updateTrialsAsync();
        toast({ type: 'success', message: t('task.createdHint') });
      }
    } catch (error) {
      if (error instanceof OfflineAvatarTaskError && error.reason) {
        handleOfflineAvatarSubmitError(
          { code: error.code ?? 0, reason: error.reason, message: error.message, metadata: error.metadata },
          getDefaultReportParams({
            value: buildOfflineAvatarTaskParams(taskInput, { avatarId: selectedAvatarAssetId ?? undefined }),
            isInspireMe: false,
          }),
        );
        return;
      }
      toast({ type: 'error', message: t('error.submit') });
    } finally {
      submissionLock.current = false;
      setPreparing(false);
    }
  };

  const generateButton = (
    <div className="sm:w-full">
      <CustomTooltip
        content={generateTooltipMessage}
        {...(generateClickValidationMessage
          ? {
              open: actionRequiredTooltipOpen,
              onOpenChange: setActionRequiredTooltipOpen,
              trigger: 'click' as const,
            }
          : { trigger: 'hover' as const })}
        side="top"
        sideOffset={8}
      >
        <span className="block sm:w-full">
          <button
            type="button"
            onClick={submit}
            disabled={!canClickGenerate || submitting || submitSucceeded}
            aria-label={submitSucceeded ? t('submitted') : undefined}
            className={cn(
              'relative flex min-h-12 items-center justify-center gap-3 rounded-full bg-system-white px-7 font-semibold text-sm text-system-black transition-opacity hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-system-blue01 focus-visible:outline-offset-4 disabled:cursor-not-allowed motion-reduce:transition-none sm:w-full sm:px-5',
              submitSucceeded ? 'disabled:opacity-100' : 'disabled:opacity-30',
            )}
          >
            <span
              aria-hidden={submitSucceeded}
              className={cn('flex items-center gap-3', submitSucceeded && 'invisible')}
            >
              {t(submitting ? 'generating' : 'generate')}
              <ArrowRight className="size-4" aria-hidden="true" />
            </span>
            {submitSucceeded ? (
              <span
                data-testid="submit-success-animation"
                className="offline-avatar-submit-success absolute inset-0 flex items-center justify-center"
              >
                <span className="offline-avatar-success-firework" aria-hidden="true">
                  {['n', 'ne', 'e', 'se', 's', 'sw', 'w', 'nw'].map((direction) => (
                    <i key={direction} />
                  ))}
                  <svg viewBox="0 0 32 32" className="size-8" fill="none">
                    <circle cx="16" cy="16" r="14" fill="#10B981" />
                    <path
                      d="m9.5 16.5 4.25 4.25L23 11.5"
                      stroke="white"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </span>
            ) : null}
          </button>
        </span>
      </CustomTooltip>
    </div>
  );

  /** 三个步骤的内容区：横向并排，按当前步骤左右滑动；只有当前这一屏可聚焦、可被读屏读到 */
  const panels: { key: string; content: ReactNode }[] = [
    {
      key: 'avatar',
      content: (
        <div className="flex min-h-0 flex-1 flex-col pt-3 sm:pt-2">
          <AvatarGallery
            isLoggedIn={Boolean(userId)}
            isUserInfoInitialized={isUserInfoInitialized}
            selectedId={selectedAvatar}
            maxCardHeight={stageHeight}
            officialAvatars={officialAvatars}
            officialAvatarsLoading={officialAvatarsLoading}
            userAvatars={userAvatars}
            userAvatarsLoading={userAvatarsLoading}
            onSelect={selectAvatar}
            onCreateAvatar={() => void openCreateAvatar()}
            onLogin={openLoginDialog}
            onEditAvatar={editAvatar}
            onDeleteAvatar={(avatar) => void deleteAvatar(avatar)}
            onConfirm={(avatar) => {
              selectAvatar(avatar);
              navigate(1);
            }}
          />
          <CreateAvatarDialog
            open={createAvatarOpen}
            onOpenChange={(open) => {
              setCreateAvatarOpen(open);
              if (!open) setEditingAvatar(null);
            }}
            editingAvatar={editingAvatar}
            onCreated={() => {
              void refreshUserAvatars();
            }}
          />
        </div>
      ),
    },
    {
      key: 'speech',
      content: (
        <>
          <div className="flex h-full max-h-[560px] min-h-0 w-full gap-6 px-8 pt-3 sm:max-h-none sm:flex-1 sm:flex-col sm:gap-4 sm:px-0 sm:pt-0">
            {imagePreview ? (
              <SelectedAvatar
                src={imagePreview}
                height={promptBoxHeight}
                onReplace={() => {
                  if (taskService.generatingSpeech) {
                    notifyTtsGeneratingLock();
                    return;
                  }
                  navigate(0);
                }}
                className="sm:hidden"
              />
            ) : null}
            <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden pb-4">
              <SpeechComposer
                mode={speechMode}
                onModeChange={setSpeechMode}
                script={script}
                onScriptChange={setScript}
                voice={selectedVoice}
                onVoiceChange={setSelectedVoice}
                audioPrompt={media.audioPrompt}
                recordedPrompt={media.recordedPrompt}
                onAudioUpload={media.onAudioUpload}
                onRemoveAudio={() => media.setAudioPrompt(null)}
                onRecording={media.onRecording}
                onRemoveRecording={() => media.setRecordedPrompt(null)}
                onBusyChange={setCaptureBusy}
                onBoxHeightChange={setPromptBoxHeight}
                onNext={() => void continueFromSpeech()}
                nextDisabled={
                  !ready[0] || captureBusy || taskService.generatingSpeech || (speechMode !== 'text' && !speechReady)
                }
                nextLoading={taskService.generatingSpeech}
                nextValidationMessage={textValidationMessage}
                locked={taskService.generatingSpeech}
                onLocked={notifyTtsGeneratingLock}
              />
            </div>
          </div>
        </>
      ),
    },
    {
      key: 'settings',
      content: (
        <>
          <div className="flex h-full max-h-[560px] min-h-0 w-full gap-6 px-8 pt-3 sm:max-h-none sm:flex-1 sm:flex-col sm:gap-4 sm:px-0 sm:pt-0">
            {imagePreview ? (
              <SelectedAvatar
                src={imagePreview}
                height={promptBoxHeight}
                onReplace={() => navigate(0)}
                className="sm:hidden"
              />
            ) : null}
            <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden pb-4">
              <h2
                data-settings-header
                className="mb-5 flex h-[45px] shrink-0 items-center border-system-white08 border-b font-medium text-sm text-system-white80"
              >
                {t('settings.title')}
              </h2>
              <div data-settings-scroll className="min-h-0 flex-1 overflow-y-auto">
                <div data-settings-module className="space-y-6">
                  <div data-output-settings-module>
                    <OfflineDigitalHumanSettings
                      settings={output}
                      onChange={(patch) => setOutput((current) => ({ ...current, ...patch }))}
                    />
                  </div>
                  <div data-actions-settings-module>
                    <section aria-label={t('actions.title')}>
                      <h2 className="mb-3 font-medium text-sm text-system-white80">{t('actions.title')}</h2>
                      <div className="grid grid-cols-2 gap-3">
                        {(['auto', 'custom'] as const).map((mode) => (
                          <button
                            key={mode}
                            type="button"
                            aria-pressed={mode === actionMode}
                            onClick={() => changeActionMode(mode)}
                            className={cn(
                              'flex min-h-14 items-center rounded-12 border px-4 py-3 text-left transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-system-blue01 focus-visible:outline-offset-2 motion-reduce:transition-none',
                              mode === actionMode
                                ? 'border-system-blue01/70 bg-system-blue01/10 text-system-white'
                                : 'border-system-white12 bg-system-white02 text-system-white64 hover:border-system-white32',
                            )}
                          >
                            <span className="flex items-center gap-2 font-medium text-sm">
                              {mode === 'auto' ? <Sparkles className="size-4" aria-hidden="true" /> : null}
                              {t(`actions.${mode}`)}
                              <CustomTooltip
                                content={t(`actions.${mode}Hint`)}
                                side="top"
                                align="center"
                                trigger="auto"
                              >
                                <span
                                  className="inline-flex cursor-help text-system-white40 transition-colors hover:text-system-white80"
                                  onClick={(event) => event.stopPropagation()}
                                >
                                  <InfoIcon className="size-4" aria-hidden="true" />
                                </span>
                              </CustomTooltip>
                            </span>
                          </button>
                        ))}
                      </div>
                    </section>
                  </div>
                </div>
              </div>
              <div
                data-settings-footer
                className="flex shrink-0 items-center justify-end gap-3 bg-system-bg01 sm:flex-col sm:pb-[env(safe-area-inset-bottom)]"
              >
                {hasSubmittedTask ? (
                  <Link
                    href="/vidu-stream/profile/offline-avatar"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex min-h-12 items-center justify-center rounded-full border border-system-white20 px-7 font-semibold text-sm text-system-white transition-colors hover:bg-system-white08 focus-visible:outline focus-visible:outline-2 focus-visible:outline-system-blue01 focus-visible:outline-offset-4 sm:w-full sm:px-5"
                  >
                    {t('viewCreations')}
                  </Link>
                ) : null}
                {generateButton}
              </div>
            </div>
          </div>
        </>
      ),
    },
  ];

  return (
    <>
      <div
        ref={scrollRef}
        id={scrollId}
        data-offline-avatar-scroll-root
        className="h-full min-h-0 w-full overflow-hidden bg-system-bg01 px-8 pb-6 sm:px-4 sm:pb-4 md:px-5"
      >
        <div className="mx-auto h-full min-h-[520px] w-full max-w-[1440px] sm:min-h-0">
          <div className="flex h-full min-h-[520px] flex-col sm:min-h-0">
            <div data-mobile-fixed-header className="shrink-0">
              <div className="relative">
                <StudioHero />
                <Link
                  href="/vidu-stream/profile/offline-avatar"
                  onClick={(event) => {
                    if (userId) return;
                    event.preventDefault();
                    openLoginDialog();
                  }}
                  className="absolute top-[18%] left-3 z-10 inline-flex h-10 items-center gap-2 rounded-full border border-white/20 bg-black/20 px-4 font-medium text-sm text-white shadow-lg backdrop-blur-md transition-colors hover:bg-black/35 focus-visible:outline focus-visible:outline-2 focus-visible:outline-system-blue01 focus-visible:outline-offset-2 sm:top-6 sm:left-2 sm:h-7 sm:gap-1.5 sm:px-2.5 sm:text-[10px] sm:leading-none"
                >
                  <AgentLineHistory className="size-4 sm:size-3.5" aria-hidden="true" />
                  {t('historyWorks')}
                </Link>
              </div>

              <StudioSteps
                step={step}
                ready={[ready[0], speechReady && !captureBusy]}
                onChange={changeStep}
                overlapBanner
              />
            </div>

            <div ref={stageRef} className="relative mt-4 min-h-0 flex-1 overflow-hidden">
              <div
                className="flex h-full transition-transform duration-500 ease-out motion-reduce:transition-none"
                style={{ transform: `translateX(-${step * 100}%)` }}
              >
                {panels.map((panel, index) => (
                  <section
                    key={panel.key}
                    data-step-panel={index}
                    ref={index === step ? composerRef : undefined}
                    tabIndex={-1}
                    aria-hidden={index !== step}
                    inert={index !== step}
                    aria-label={t(`steps.${panel.key}.title`)}
                    className="flex h-full w-full min-w-0 shrink-0 flex-col outline-none focus-visible:ring-1 focus-visible:ring-system-blue01"
                  >
                    {panel.content}
                  </section>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Dialog
        open={actionTimelineOpen}
        onOpenChange={(open) => (open ? setActionTimelineOpen(true) : closeActionTimeline())}
      >
        <DialogContent
          ref={actionTimelineDialogRef}
          variant="mobileFullScreen"
          className="grid h-[min(760px,calc(100dvh-32px))] max-w-[720px] grid-rows-[auto_minmax(0,1fr)_auto] gap-0 overflow-hidden border-system-white12 bg-[#1c1c1c] p-0 sm:h-dvh sm:max-h-none sm:gap-0 sm:overflow-hidden sm:pb-0"
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            actionTimelineDialogRef.current?.focus({ preventScroll: true });
          }}
        >
          <div className="flex shrink-0 items-baseline gap-2 border-system-white08 border-b px-6 py-5 pr-14 md:px-4 md:py-4">
            <DialogTitle className="text-left text-white text-xl">{t('actions.custom')}</DialogTitle>
            {timelineDuration !== undefined ? (
              <span className="shrink-0 font-normal text-sm text-system-white48 tabular-nums">
                {t('actions.maxDuration', { duration: timelineDuration })}
              </span>
            ) : null}
          </div>
          <div className="min-h-0">
            <InlineActionTimeline
              actions={draftActions}
              duration={timelineDuration}
              issues={draftActionIssues}
              onChange={(id, patch) => {
                const nextActions = draftActions.map((action) => (action.id === id ? { ...action, ...patch } : action));
                setDraftActions(nextActions);
                if (draftValidationActive) {
                  setDraftActionIssues(validateStudioActionIssues(nextActions, timelineDuration));
                }
                setDraftValidationTooltipOpen(false);
              }}
              onDelete={(id) => {
                const nextActions = draftActions.filter((action) => action.id !== id);
                setDraftActions(nextActions);
                if (draftValidationActive) {
                  setDraftActionIssues(validateStudioActionIssues(nextActions, timelineDuration));
                }
                setDraftValidationTooltipOpen(false);
              }}
              onAdd={() => {
                const nextActions = appendStudioAction(draftActions, crypto.randomUUID(), timelineDuration);
                setDraftActions(nextActions);
                if (draftValidationActive) {
                  setDraftActionIssues(validateStudioActionIssues(nextActions, timelineDuration));
                }
                setDraftValidationTooltipOpen(false);
              }}
            />
          </div>
          <div className="flex shrink-0 justify-end gap-3 border-system-white08 border-t px-6 py-4 sm:pb-[calc(16px+env(safe-area-inset-bottom))] md:px-4">
            <Button type="button" variant="outline" size="dialog" className="min-w-24" onClick={closeActionTimeline}>
              {tCommon('cancel')}
            </Button>
            <CustomTooltip
              content={
                draftActionIssues.length > 1 ? (
                  <ol className="list-decimal space-y-1 pl-5">
                    {draftActionIssues.map((issue) => (
                      <li key={issue}>{t(`actions.${issue}`)}</li>
                    ))}
                  </ol>
                ) : draftActionIssues.length === 1 ? (
                  t(`actions.${draftActionIssues[0]}`)
                ) : null
              }
              open={draftValidationTooltipOpen}
              onOpenChange={setDraftValidationTooltipOpen}
              trigger="click"
              side="top"
              sideOffset={8}
            >
              <Button type="button" variant="solid" size="dialog" className="min-w-24" onClick={confirmActionTimeline}>
                {tCommon('confirm')}
              </Button>
            </CustomTooltip>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** 第 2、3 步左栏：已选角色的竖版照片（与右侧提示词框等高），右上角是更换入口 */
function SelectedAvatar({
  src,
  height,
  onReplace,
  className,
}: {
  src: string;
  height?: number;
  onReplace: () => void;
  className?: string;
}) {
  const t = useTranslations('tools.offlineDigitalHuman.studio');
  return (
    <aside className={cn('flex shrink-0 flex-col sm:flex-row sm:items-center sm:gap-3', className)}>
      {/* 标题行与右侧 tab 行等高（上下 12px + 20px 行高 + 1px 底线），下方 20px 对应 tab 的 mb-5，照片顶部与提示词框顶部对齐 */}
      <p className="mb-5 flex h-[45px] shrink-0 items-center font-medium text-sm text-system-white80 sm:hidden">
        {t('avatar.selected')}
      </p>
      <div
        className="sm:!h-auto sm:!w-16 relative aspect-[3/4] shrink-0"
        style={{ height: height ?? 267, width: 'auto' }}
      >
        <img
          src={src}
          alt={t('avatar.selected')}
          className="size-full rounded-16 border-2 border-system-white20 object-cover object-top sm:rounded-10"
        />
        <button
          type="button"
          onClick={onReplace}
          aria-label={t('avatar.replace')}
          title={t('avatar.replace')}
          className="absolute top-2 right-2 flex size-8 items-center justify-center rounded-full bg-black text-white transition-opacity hover:opacity-80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-system-blue01 sm:top-1 sm:right-1 sm:size-6"
        >
          <ArrowRightLeft size={16} strokeWidth={1.8} aria-hidden="true" />
        </button>
      </div>
      <span className="hidden font-medium text-sm text-system-white80 sm:block">{t('avatar.selected')}</span>
    </aside>
  );
}
